// agentLoop.ts — ReAct agent loop for /ai.
//
// What changed from the pipeline version:
//   BEFORE: retrieve → rule-based tool dispatch → single LLM call → check.
//   NOW:    retrieve → loop { LLM picks Thought+Action → execute → Observe }
//           until LLM picks "ANSWER" or step budget exhausted.
//
// Pattern: ReAct (Yao et al. 2022). The model alternates "reasoning" and
// "acting" in a tight loop. Each iteration is logged as a distinct
// THOUGHT / ACTION / OBSERVATION step that the trace panel surfaces —
// so the visitor sees an agent thinking, not a pipeline executing.
//
// Why this works with Qwen-1.5B (a small model):
//   - Strict structured output format (THOUGHT/ACTION lines)
//   - Tight step budget (max 4 iterations)
//   - Defensive parsing with fallback to rule-based dispatch on parse failure
//   - Tool catalogue kept small and named exactly (matches our MCP tools)
//
// Reference: Yao et al. 2022, "ReAct: Synergizing Reasoning and Acting in
// Language Models", https://arxiv.org/abs/2210.03629

import type { Chunk } from "@/data/corpus";
import { mcpCall, type MCPCallResult } from "@/lib/mcpTransport";
import { BROWSER_TOOLS } from "@/lib/browserTools";

const MAX_STEPS = 4;

export type AgentMessage = {
  role: "system" | "user" | "assistant";
  content: string;
};

export type ActionChoice =
  | { kind: "tool"; name: string; args: Record<string, unknown> }
  | { kind: "answer"; text: string }
  | { kind: "noop" };

export type AgentStep = {
  iteration: number;
  thought: string;
  action: ActionChoice;
  observation?: string;            // human-readable for trace
  observationData?: unknown;       // raw tool result
  mcpResult?: MCPCallResult;       // the JSON-RPC envelope when action was a tool
  parseFailed?: boolean;
  rawLLMOutput?: string;           // for debugging
};

export type AgentRunResult = {
  steps: AgentStep[];
  answer: string;
  stopReason: "answer" | "max-steps" | "loop-detected" | "model-error" | "no-progress";
};

// LLM caller abstraction — keeps this module independent of any specific
// LLM hook. AI.tsx will pass in a closure that calls useGenerativeLLM.
export type LLMCaller = (
  messages: AgentMessage[],
  opts: { maxTokens?: number; temperature?: number; onToken?: (s: string) => void },
) => Promise<string>;

// Callbacks the page uses to render trace cards in real time.
export type AgentCallbacks = {
  onStepBegin?: (iter: number) => void;
  onThought?: (iter: number, thought: string) => void;
  onAction?: (iter: number, action: ActionChoice) => void;
  onObservation?: (
    iter: number,
    observation: string,
    mcpResult?: MCPCallResult,
  ) => void;
  onTokenStream?: (iter: number, delta: string) => void; // for streaming THOUGHT in UI
  onAnswerStream?: (delta: string) => void; // for streaming the final answer to chat + TTS
};

// ─────────────────────────── prompt ───────────────────────────

function toolCatalogue(): string {
  return Object.values(BROWSER_TOOLS)
    .map((t) => `- ${t.name}(${Object.keys(t.inputSchema.properties).join(", ")}): ${t.description.split(".")[0]}.`)
    .join("\n");
}

function buildSystemPrompt(): string {
  return `You are JoshLM, an AI agent that answers questions about Harsh Joshi.

You can think then act. Always respond in this EXACT format (no preamble, no code blocks):

THOUGHT: <one sentence: what do you need to do or check next?>
ACTION: <one of the two formats below>
  TOOL <toolName> <json args>
  ANSWER <your final answer, 2-4 sentences, cite chunks with [chunk-id]>

Available tools (use these exact names):
${toolCatalogue()}

Rules:
1. Use TOOL only when the context lacks the answer and a tool can provide live data.
2. Use ANSWER as soon as the context (chunks + tool results so far) is enough.
3. Be brief in THOUGHT — one sentence max.
4. For ACTION TOOL, json args must be valid JSON.
5. For ACTION ANSWER, ground in chunks/tool data; cite chunks using [chunk-id] inline.
6. If the context cannot answer, ANSWER with: "I don't have that in my knowledge base."
7. Speak about Harsh in third person.`;
}

function buildContext(
  chunks: Chunk[],
  observations: AgentStep[],
): string {
  const chunkBlock = chunks
    .slice(0, 5)
    .map((c) => `[${c.id}] ${c.text}`)
    .join("\n");
  const obsBlock = observations
    .filter((s) => s.action.kind === "tool" && s.observation)
    .map(
      (s) =>
        `[Observed ${(s.action as { kind: "tool"; name: string }).name}] ${s.observation}`,
    )
    .join("\n");
  return obsBlock
    ? `Chunks:\n${chunkBlock}\n\nObservations so far:\n${obsBlock}`
    : `Chunks:\n${chunkBlock}`;
}

// ─────────────────────────── parsing ───────────────────────────

// Defensive parser. Small models often:
//   - omit THOUGHT (we synthesise one)
//   - emit lowercase "thought" / "action"
//   - emit ACTION on same line as ACTION: keyword
//   - wrap json in markdown fences
//   - emit free text after ANSWER
export function parseAgentStep(raw: string): {
  thought: string;
  action: ActionChoice;
  parseFailed?: boolean;
} {
  const text = raw.trim();
  if (!text) return { thought: "", action: { kind: "noop" }, parseFailed: true };

  // Strip markdown fences
  const clean = text.replace(/```(?:json)?/g, "").trim();

  // Find THOUGHT
  const thoughtMatch =
    /thought\s*[:\-]\s*(.+?)(?=\n\s*(?:action|tool|answer)\b|$)/is.exec(clean);
  const thought = thoughtMatch?.[1]?.trim() ?? "";

  // Find ACTION block
  // Prefer ACTION TOOL <name> <json>
  const toolMatch =
    /(?:action\s*[:\-]\s*)?\btool\b\s+([a-zA-Z]\w+)\s*([\s\S]*)$/i.exec(clean);
  if (toolMatch) {
    const name = toolMatch[1].trim();
    if (!BROWSER_TOOLS[name]) {
      return {
        thought,
        action: { kind: "noop" },
        parseFailed: true,
      };
    }
    // Greedy match of first balanced {...} after the tool name
    const argsText = toolMatch[2].trim();
    const braceStart = argsText.indexOf("{");
    if (braceStart === -1) {
      return {
        thought,
        action: { kind: "noop" },
        parseFailed: true,
      };
    }
    // Naive but works for typical JSON: find matching brace by scanning
    let depth = 0;
    let braceEnd = -1;
    for (let i = braceStart; i < argsText.length; i++) {
      if (argsText[i] === "{") depth++;
      else if (argsText[i] === "}") {
        depth--;
        if (depth === 0) {
          braceEnd = i;
          break;
        }
      }
    }
    if (braceEnd === -1) {
      return { thought, action: { kind: "noop" }, parseFailed: true };
    }
    const jsonStr = argsText.slice(braceStart, braceEnd + 1);
    let args: Record<string, unknown> = {};
    try {
      args = JSON.parse(jsonStr);
    } catch {
      return { thought, action: { kind: "noop" }, parseFailed: true };
    }
    return { thought, action: { kind: "tool", name, args } };
  }

  // ANSWER form
  const answerMatch = /(?:action\s*[:\-]\s*)?\banswer\b\s*[:\-]?\s*([\s\S]+)$/i.exec(clean);
  if (answerMatch) {
    const answerText = answerMatch[1].trim();
    return { thought, action: { kind: "answer", text: answerText } };
  }

  // Fallback: nothing matched. Treat the whole text as the answer if it's not
  // obvious format garbage. This protects against models that just write
  // a sentence without scaffolding.
  if (text.length > 10 && !text.toLowerCase().includes("tool")) {
    return { thought: thought || "(no explicit thought)", action: { kind: "answer", text } };
  }

  return { thought, action: { kind: "noop" }, parseFailed: true };
}

// ─────────────────────────── observation formatting ───────────────────────────

// Convert a tool's raw data into a short human-readable observation the LLM
// can reason about. This is the "interpreted result" the agent reads back
// before deciding next action.
export function summariseObservation(
  toolName: string,
  data: unknown,
): string {
  if (data == null || typeof data !== "object") return String(data ?? "(empty)");
  const d = data as Record<string, unknown>;
  switch (toolName) {
    case "getGithubRepo":
      return `${d.fullName} · ${d.stars} ★ · language ${d.language ?? "unknown"} · last push ${d.pushedAt ?? "?"}`;
    case "getNpmPackage":
      return `${d.name} v${d.latestVersion} · ${d.weeklyDownloads ?? "?"} weekly downloads`;
    case "getPyPIPackage":
      return `${d.name} v${d.latestVersion} · ${(d.summary as string)?.slice(0, 80) ?? ""}`;
    case "getAppStoreApp":
      return `${d.name} v${d.version} · ${d.averageUserRating ?? "—"}★ (${d.userRatingCount ?? 0} ratings)`;
    case "findOpenAIRoles": {
      const roles = (d.roles as Array<{ title: string; location: string }>) ?? [];
      return `${d.matchCount} roles · ${roles.slice(0, 3).map((r) => `${r.title} (${r.location})`).join("; ")}`;
    }
    case "getSubstackPosts": {
      const posts = (d.posts as Array<{ title: string }>) ?? [];
      return `${posts.length} posts · ${posts.slice(0, 2).map((p) => p.title).join("; ")}`;
    }
    case "getResumeSection":
      return JSON.stringify(d).slice(0, 200);
    default:
      return JSON.stringify(d).slice(0, 200);
  }
}

// ─────────────────────────── the loop ───────────────────────────

type DetectedActionInsight = {
  reasonForStop: AgentRunResult["stopReason"];
  duplicateActionKey?: string;
};

export async function runAgentLoop(opts: {
  query: string;
  history: AgentMessage[];
  chunks: Chunk[];
  llmCall: LLMCaller;
  callbacks?: AgentCallbacks;
}): Promise<AgentRunResult> {
  const { query, history, chunks, llmCall, callbacks } = opts;
  const systemPrompt = buildSystemPrompt();
  const steps: AgentStep[] = [];
  const actionKeysSeen = new Set<string>();

  for (let i = 0; i < MAX_STEPS; i++) {
    const iteration = i + 1;
    callbacks?.onStepBegin?.(iteration);

    const context = buildContext(chunks, steps);
    const messages: AgentMessage[] = [
      { role: "system", content: systemPrompt },
      ...history,
      {
        role: "user",
        content: `${context}\n\nQuestion: ${query}\n\nRespond now with THOUGHT and ACTION:`,
      },
    ];

    const raw = await llmCall(messages, {
      maxTokens: 220,
      temperature: 0.2,
      onToken: (s) => callbacks?.onTokenStream?.(iteration, s),
    });

    const parsed = parseAgentStep(raw);
    if (parsed.thought) callbacks?.onThought?.(iteration, parsed.thought);

    // Loop detection: if the model wants to repeat a tool call it already
    // did, force it to answer instead.
    let action = parsed.action;
    if (action.kind === "tool") {
      const key = `${action.name}::${JSON.stringify(action.args)}`;
      if (actionKeysSeen.has(key)) {
        action = { kind: "noop" };
      } else {
        actionKeysSeen.add(key);
      }
    }

    callbacks?.onAction?.(iteration, action);

    // Step recorded; details get attached after execution
    const step: AgentStep = {
      iteration,
      thought: parsed.thought,
      action,
      rawLLMOutput: raw,
      parseFailed: parsed.parseFailed,
    };
    steps.push(step);

    if (parsed.parseFailed || action.kind === "noop") {
      // Force answer mode for the NEXT iteration via a directive observation.
      step.observation = parsed.parseFailed
        ? "(parse error — I'll answer directly using only the chunks)"
        : "(no progress — answering from current context)";
      callbacks?.onObservation?.(iteration, step.observation);
      // Synthesise a final answer from the chunks as a safety net
      const fallbackAnswer = synthesiseFromChunks(chunks);
      callbacks?.onAnswerStream?.(fallbackAnswer);
      return {
        steps,
        answer: fallbackAnswer,
        stopReason: parsed.parseFailed ? "model-error" : "no-progress",
      };
    }

    if (action.kind === "answer") {
      const finalAnswer = action.text;
      callbacks?.onAnswerStream?.(finalAnswer);
      return { steps, answer: finalAnswer, stopReason: "answer" };
    }

    if (action.kind === "tool") {
      const mcp = await mcpCall(action.name, action.args);
      step.mcpResult = mcp;
      step.observationData = mcp.data;
      step.observation = mcp.ok
        ? summariseObservation(action.name, mcp.data)
        : `tool error: ${mcp.error}`;
      callbacks?.onObservation?.(iteration, step.observation, mcp);
    }
  }

  // Max steps hit without a final answer — synthesise one from context
  const finalAnswer = synthesiseFromChunks(chunks, steps);
  callbacks?.onAnswerStream?.(finalAnswer);
  return { steps, answer: finalAnswer, stopReason: "max-steps" };
}

// ─────────────────────────── safety-net synthesiser ───────────────────────────

// If the agent loop fails, stitch a minimal answer from the retrieved chunks
// so the user always gets something coherent.
function synthesiseFromChunks(chunks: Chunk[], steps: AgentStep[] = []): string {
  if (chunks.length === 0) {
    return "I don't have anything matching that question in my knowledge base.";
  }
  const lead = chunks[0].text;
  const observationBits = steps
    .filter((s) => s.observation && s.action.kind === "tool")
    .map((s) => s.observation as string)
    .slice(0, 2)
    .join(" · ");
  return observationBits ? `${lead} (${observationBits})` : lead;
}
